import { useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TimeEntryTypeLabel } from "@/types/timeEntry";
import { getSelectSectors } from "@/Api/sectors";
import type { UnityFilterState } from "@/components/UnityFilter";
import { useState } from "react";

const pad = (n: number) => String(n).padStart(2, "0");
const toInput = (d: Date) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0);
const endOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59);

export const PERIODS: Record<string, string> = {
  today: "Hoje",
  yesterday: "Ontem",
  week: "Últimos 7 dias",
  month: "Este mês",
  custom: "Personalizado",
};

export const periodRange = (period: string): { start_at: string; end_at: string } | null => {
  const now = new Date();
  switch (period) {
    case "today":
      return { start_at: toInput(startOfDay(now)), end_at: toInput(endOfDay(now)) };
    case "yesterday": {
      const y = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
      return { start_at: toInput(startOfDay(y)), end_at: toInput(endOfDay(y)) };
    }
    case "week": {
      const s = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);
      return { start_at: toInput(startOfDay(s)), end_at: toInput(endOfDay(now)) };
    }
    case "month": {
      const s = new Date(now.getFullYear(), now.getMonth(), 1);
      return { start_at: toInput(startOfDay(s)), end_at: toInput(endOfDay(now)) };
    }
    default:
      return null;
  }
};

/** Valores iniciais: dia de hoje, da primeira à última hora. */
export const defaultTimeEntryFilters = (): Record<string, string> => ({
  period: "today",
  ...(periodRange("today") as Record<string, string>),
});

const ALL = "all";

export const TimeEntriesExtraFilters = ({ filter }: { filter: UnityFilterState }) => {
  const { extra, setExtra, selected } = filter;
  const [sectors, setSectors] = useState<{ id: number; name: string }[]>([]);

  // Setores são cadastrados por unidade: a lista acompanha as unidades escolhidas.
  useEffect(() => {
    setExtra("sector_id", "");
    (async () => {
      const { data } = await getSelectSectors(selected.length ? selected.join(",") : undefined);
      setSectors(data?.response ?? []);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected.join(",")]);

  const selectField = (
    key: string,
    label: string,
    placeholder: string,
    options: { value: string; label: string }[]
  ) => (
    <div className="space-y-1.5 w-full sm:w-60">
      <Label className="text-sm">{label}</Label>
      <Select
        value={extra[key] || ALL}
        onValueChange={(v) => setExtra(key, v === ALL ? "" : v)}
      >
        <SelectTrigger className="h-9 w-full">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{placeholder}</SelectItem>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  const onPeriod = (period: string) => {
    setExtra("period", period);
    const range = periodRange(period);
    if (range) {
      setExtra("start_at", range.start_at);
      setExtra("end_at", range.end_at);
    }
  };

  return (
    <>
      {selectField(
        "time_entry_type_id",
        "Tipo",
        "Todos os tipos",
        Object.entries(TimeEntryTypeLabel).map(([value, label]) => ({ value, label }))
      )}
      {selectField(
        "sector_id",
        "Setor",
        "Todos os setores",
        sectors.map((s) => ({ value: String(s.id), label: s.name }))
      )}
      {selectField("is_late", "Atraso", "Todos", [
        { value: "1", label: "Com atraso" },
        { value: "0", label: "Sem atraso" },
      ])}
      <div className="space-y-1.5 w-full sm:w-60">
        <Label className="text-sm">Período</Label>
        <Select value={extra.period || "custom"} onValueChange={onPeriod}>
          <SelectTrigger className="h-9 w-full">
            <SelectValue placeholder="Período" />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(PERIODS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5 w-full sm:w-60">
        <Label className="text-sm">Data/hora inicial</Label>
        <Input
          type="datetime-local"
          className="h-9"
          value={extra.start_at || ""}
          onChange={(e) => {
            setExtra("period", "custom");
            setExtra("start_at", e.target.value);
          }}
        />
      </div>
      <div className="space-y-1.5 w-full sm:w-60">
        <Label className="text-sm">Data/hora final</Label>
        <Input
          type="datetime-local"
          className="h-9"
          value={extra.end_at || ""}
          onChange={(e) => {
            setExtra("period", "custom");
            setExtra("end_at", e.target.value);
          }}
        />
      </div>
    </>
  );
};
